'use client';

import {
  createContext,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { indexes } from '@/algolia';
import { EVENT_NAMES, trackEvent } from '@/analytics';
import type { SiteSearchListContextType } from '@/analytics/events';
import { getValidRecentSearches } from '@/components/SiteSearch';
import { topicPath } from '@/helpers/linkHelpers';
import { safeLocalStorage } from '@/lib/safeLocalStorage';
import type { TopicFirestoreType } from '@/types/firestore/TopicFirestoreType';

const topicsIndex = () => {
  return indexes.topics;
};

type SearchPairType = {
  slug: string;
  name: string;
};

interface HandleTopicSelectionProps {
  name: string;
  slug: string;
  updateRecent: boolean;
  listContext: SiteSearchListContextType;
  listIndex?: number;
}

export interface SiteSearchContextProps {
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  results: TopicFirestoreType[];
  search: (query: string) => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  inputIsVisible: boolean;
  setInputIsVisible: Dispatch<SetStateAction<boolean>>;
  handleTopicSelection: ({
    name,
    slug,
    updateRecent,
    listContext,
  }: HandleTopicSelectionProps) => void;
  recentSearches: SearchPairType[] | null;
  setRecentSearches?: Dispatch<SetStateAction<SearchPairType[] | null>>;
  activeIndex: number | null;
  setActiveIndex: Dispatch<SetStateAction<number | null>>;
  inputRef?: RefObject<HTMLInputElement>;
}

export const SiteSearchContext = createContext<
  SiteSearchContextProps | undefined
>(undefined);

export const MAX_SEARCH_RESULTS_TO_SHOW = 12;
export const MAX_RECENT_SEARCHES_TO_SHOW = 10;

export function SiteSearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState<string>('');
  // biome-ignore lint/suspicious/noExplicitAny: TODO update when search page is overhauled
  const [results, setResults] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputIsVisible, setInputIsVisible] = useState<boolean>(false);
  const [recentSearches, setRecentSearches] = useState<SearchPairType[] | null>(
    null,
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function updateRecentSearches(newSearchPair: SearchPairType) {
    const currentRecentSearches = getValidRecentSearches();

    const matchingSearch = currentRecentSearches?.find(
      (recentSearch: SearchPairType) =>
        recentSearch.slug === newSearchPair.slug,
    );

    if (!matchingSearch) {
      const newArray = [newSearchPair, ...currentRecentSearches];
      safeLocalStorage.setItem('recent-searches', JSON.stringify(newArray));
      setRecentSearches(newArray);
    }
  }

  function handleTopicSelection({
    name,
    slug,
    updateRecent,
    listContext,
    listIndex,
  }: HandleTopicSelectionProps) {
    setQuery('');
    setIsOpen(false);
    trackEvent(EVENT_NAMES.search_dropdown_topic_click, {
      context: listContext,
      topic_path: topicPath(slug),
      list_position_index: listIndex?.toString(),
    });

    if (updateRecent) {
      updateRecentSearches({ name, slug });
    }
  }

  useEffect(() => {
    if (safeLocalStorage.getItem('recent-searches')) {
      const localStorageRecentSearches = getValidRecentSearches();

      const trimmedResults = localStorageRecentSearches.slice(
        0,
        MAX_RECENT_SEARCHES_TO_SHOW,
      );

      setRecentSearches(trimmedResults);
    }
  }, []);

  /**
   * WARNING: This search is done without any debounce, but will leverage a client
   * cache if initialized. Search here should only be done when there's clear user
   * intent (e.g. a pause or button click) to avoid flooding Algolia with queries.
   */
  function search(query: string): void {
    setIsOpen(true);

    if (query.length > 0) {
      console.info('ALGOLIA MAIN SEARCH', query);

      topicsIndex()
        .search<TopicFirestoreType>(query, {
          hitsPerPage: MAX_SEARCH_RESULTS_TO_SHOW,
          filters: 'public:true',
        })
        .then(({ hits }) => {
          setResults(hits);
        });
    } else {
      setResults([]);
    }
  }

  return (
    <SiteSearchContext.Provider
      value={{
        handleTopicSelection,
        isOpen,
        query,
        recentSearches,
        results,
        search,
        setIsOpen,
        setQuery,
        setRecentSearches,
        activeIndex,
        setActiveIndex,
        inputIsVisible,
        setInputIsVisible,
        inputRef,
      }}
    >
      {children}
    </SiteSearchContext.Provider>
  );
}

export function useSiteSearch() {
  const context = useContext(SiteSearchContext);
  if (context === undefined) {
    throw new Error('useSiteSearch must be used within a SiteSearchProvider');
  }
  return context;
}
